import { AxiosResponse } from 'axios';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import usePrefetchPages from '../hooks/usePrefetchPages';
import { CandidateDetail } from '../types';
import SelectCandidateModal, {
  SelectCandidateFormValues,
  SelectCandidateModalProps,
} from './SelectCandidateModal';

interface PartialSelectCandidateModalProps extends Omit<SelectCandidateModalProps, 'helmetTitle'> {
  ids: CandidateDetail['id'][];
  actionOrigin: 'Banco de Talentos' | 'Triagem entre vagas';
  onSuccess?: (
    data: AxiosResponse<unknown, unknown> | undefined | void,
    variables: SelectCandidateFormValues
  ) => void | Promise<unknown>;
}

export default function PartialSelectCandidateModal({
  ids,
  actionOrigin,
  onSuccess,
  ...props
}: PartialSelectCandidateModalProps) {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const candidate = useAPIQuery([`/candidates/${ids[0]}/`], {
    enabled: ids.length === 1,
  });

  const recruitmentProcessesPages = useInfiniteAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          candidate: ids?.[0],
        },
      },
    ],
    { enabled: ids.length === 1 }
  );

  const selectedJobPostings = usePrefetchPages(recruitmentProcessesPages, (data) =>
    data.results.map((recruitmentProcess) => recruitmentProcess.job_posting.id)
  );

  const mutation = useAPIMutation(
    async ({ jobPostingId }: SelectCandidateFormValues, api) => {
      const stages = await api.get('/stages/', {
        params: {
          job_posting_id: jobPostingId,
          type: 'SELECTED',
        },
      });

      const recruitmentProcessCandidateIds: number[] = [];

      for (const candidateId of ids) {
        const recruitmentProcess = await api.get('/recruitment-processes/', {
          params: {
            job_posting: jobPostingId,
            candidate: candidateId,
          },
        });

        if (recruitmentProcess.data.count > 0) {
          recruitmentProcessCandidateIds.push(candidateId);
        }
      }

      const candidateIds = ids.filter((id) => !recruitmentProcessCandidateIds.includes(id));

      for (const candidateId of candidateIds) {
        await api.post('/recruitment-processes/', {
          candidate: candidateId,
          stage: Number(stages.data.results[0].id),
          status: 'ACTIVE',
          subscription_type: 'SELECTED',
          job_posting: jobPostingId,
          candidate_source_option: actionOrigin,
          channel:
            actionOrigin === 'Banco de Talentos' ? 'TALENT_POOL' : 'SELECTED_BETWEEN_JOB_POSTINGS',
        });
      }
    },
    {
      onSuccess,
      onSettled: async () => {
        await queryClient.invalidateQueries('useSearchCandidates');
        await queryClient.invalidateQueries('/candidate-history/');
        await queryClient.invalidateQueries(['questionnaireUserAnswers']);
        await queryClient.invalidateQueries('/recruitment-processes/');
      },
    }
  );

  return (
    <SelectCandidateModal
      loading={mutation.isLoading}
      title={
        ids.length > 1 ? (
          <span>
            <Trans>Triar</Trans>{' '}
            <strong>
              {ids.length} <Trans>candidato(s)</Trans>
            </strong>
          </span>
        ) : (
          <span>
            <Trans>Triar</Trans> <strong>{candidate.data?.name}</strong>
          </span>
        )
      }
      onFinish={(variables) => {
        mutation.mutate(variables);
      }}
      selectedJobPostings={selectedJobPostings}
      helmetTitle={
        ids.length > 1
          ? `${t('Triar')} ${ids.length} ${t('candidato(s)')}`
          : `${t('Triar')} ${candidate.data?.name}`
      }
      {...props}
    />
  );
}
